import { logger, shortUuid } from '@tunasong/models'
import type { AudioEvent, LoopEvent } from '@tunasong/schemas'
import type { FC, MouseEvent } from 'react'
import { useCallback, useState } from 'react'
import { Timeline, type TimelineProps } from '../timeline/timeline.js'
import { useLoopControl } from './loop.hook.js'

export interface EventsTimeline extends TimelineProps {
  position: number
  /** Seek to the specific position in the media */
  onSeek?(seconds: number): void
  onCreateEvent?(event: AudioEvent): void
  onUpdateEvent?(event: AudioEvent): void
  onDeleteEvent?(event: AudioEvent): void
}

/** Timeline with events control - create, remove, move events / loops on the timeline */
export const EventsTimeline: FC<EventsTimeline> = props => {
  const {
    position,
    onSeek,
    onClick,
    events = [],
    onEventClick,
    onCreateEvent,
    onUpdateEvent,
    onDeleteEvent,
    ...timelineProps
  } = props

  const [activeLoop, setActiveLoop] = useState<LoopEvent>()

  const { events: annotatedEvents } = useLoopControl({
    position,
    events,
    activeLoop,

    onLoopEnd: event => {
      if (activeLoop?.id !== event.id || !onSeek) {
        return
      }

      onSeek(event.start) // Reset to loop start
    },
  })

  const handleEventClick = useCallback(
    (ev: MouseEvent, event: AudioEvent) => {
      /** Initially call onEventClick */
      if (onEventClick) {
        onEventClick(ev, event)
      }

      if (event.type !== 'loop') {
        return
      }

      ev.preventDefault()
      ev.stopPropagation()

      /** Ctrl / meta click on a loop removes it */
      if (ev.metaKey || ev.ctrlKey) {
        // If meta or ctrl key is pressed, remove the event
        if (onDeleteEvent) {
          onDeleteEvent(event)
        }
        return
      }

      // A click on an active loop will stop it

      if (event.id === activeLoop?.id) {
        // If the loop is already active, stop it
        setActiveLoop(undefined)
        return
      }

      setActiveLoop(event)

      if (!onSeek) {
        return
      }
      onSeek(event.start)
    },
    [activeLoop?.id, onDeleteEvent, onEventClick, onSeek]
  )
  /** End loop logic */

  const handleEventChange = (event: AudioEvent) => {
    // Make sure the event is valid.
    const newEvent = { ...event }
    // 1 . start should be less than end
    if (event.end && event.start >= event.end) {
      logger.warn('Event start time must be less than end time', event)
      newEvent.end = event.start + 1 // Set end to start + 1 second
    }
    // 2. It should not overlap with existing events
    const overlappingEvent = events.find(
      e =>
        e.id !== event.id &&
        e.type === event.type &&
        e.start < (event.end ?? Infinity) &&
        (e.end ?? Infinity) > event.start
    )
    if (overlappingEvent) {
      logger.warn('Event overlaps with another event', overlappingEvent)
      // You can handle this case as needed, e.g., by adjusting the start/end times
      newEvent.start = Math.max(overlappingEvent.end ?? 0, event.start) // Adjust start to avoid overlap
    }
    if (!onUpdateEvent) {
      return
    }
    onUpdateEvent(newEvent)

    if (!onSeek) {
      return
    }
    onSeek(event.start)
  }

  const handleClick = (ev: MouseEvent, seconds: number, lane: number) => {
    if (ev.isDefaultPrevented()) {
      logger.debug('Click was already handled, ignoring')
      return
    }
    if (!onCreateEvent) {
      logger.warn('onCreateEvent is not defined, cannot handle click')
      return
    }

    // Is it a click on an existing event?
    const existingEvent = events.find(
      event => event.type === 'loop' && event.start <= seconds && (event.end ?? Infinity) > seconds
    )
    if (existingEvent) {
      return
    }
    onCreateEvent({
      id: shortUuid(),
      type: 'loop',
      name: 'New loop',
      start: seconds,
      end: seconds + 10,
    })

    if (onClick) {
      onClick(ev, seconds, lane)
    }
  }

  return (
    <Timeline
      {...timelineProps}
      events={annotatedEvents}
      onClick={handleClick}
      onUpdateEvent={handleEventChange}
      onEventClick={handleEventClick}
    />
  )
}
